<!-- Spacing to replace removed heading -->
<div class="mt-10"></div>

<!-- Product Selection (Full Width) -->
<div class="mt-5">
  <div class="-mx-2.5">
    <div class="w-full px-2.5">
      @if (initialProductChoices) {
        <div class="flex items-start justify-between relative">
          <div class="flex-1">
            <app-searchable-select
              [height]="'16'"
              [control]="form.controls.product_id"
              [searchFunction]="productSearchFunction"
              [initialOptions]="initialProductChoices"
              [showMeta]="true"
              placeholder='Search products...'
              label="Product"
              searchPlaceholder="Type to search products..."
              [minSearchLength]="2"
              [autofocus]="true">
            </app-searchable-select>
            @if (availabilityDateString) {
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Showing products available for event date: {{ availabilityDateString }}
              </p>
            }
          </div>
          @if (!filtersLocked) {
            <div class="product-filter-buttons absolute inset-y-0 right-8 z-10 flex items-center gap-1 translate-y-0.5">
              <button type="button"
                class="h-6 w-6 rounded p-0 leading-none flex items-center justify-center transition-colors"
                [ngClass]="productFilters.minSpend ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:text-blue-500'"
                (mousedown)="stopFilterClick($event)"
                (click)="toggleFilter('minSpend')"
                title="Only products that count toward minimum spend">
                <lucide-icon [name]="BadgeCheck" [size]="18"></lucide-icon>
              </button>
              <button type="button"
                class="h-6 w-6 rounded p-0 leading-none flex items-center justify-center transition-colors"
                [ngClass]="productFilters.qualifying ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:text-blue-500'"
                (mousedown)="stopFilterClick($event)"
                (click)="toggleFilter('qualifying')"
                title="Only products that qualify for £500 spend">
                <lucide-icon [name]="StarIcon" [size]="18"></lucide-icon>
              </button>
              <button type="button"
                class="h-6 w-6 rounded p-0 leading-none flex items-center justify-center transition-colors"
                [ngClass]="productFilters.offer ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:text-blue-500'"
                (mousedown)="stopFilterClick($event)"
                (click)="toggleFilter('offer')"
                title="Only products eligible for £150 offer">
                <lucide-icon [name]="GiftIcon" [size]="18"></lucide-icon>
              </button>
            </div>
          }
        </div>
      }
    </div>
  </div>
</div>

@if (showTimelineSelector && timelineItemChoices.length > 1) {
  <div class="mt-4">
    <div class="-mx-2.5">
      <div class="w-full px-2.5">
        <app-select
          [height]="'11'"
          [control]="form.controls.event_timeline_item_id"
          [choices]="timelineItemChoices"
          [label]="'Itinerary Item'"
          [placeholder]="'No itinerary item'"
          [clearable]="true">
        </app-select>
      </div>
    </div>
  </div>
}

@if (!selectedProduct) {
  <div class="-mx-2.5 mt-5">
    <div class="w-1/2 px-2.5">
      <app-text-input [control]="disabledControl" label="Qty / Amount" [placeholder]="'Select a product..'"></app-text-input>
    </div>
  </div>
}

<!-- Form Fields (Single Column) -->
@if (shouldShowPricingSection) {
  <div class="-mx-2.5 mt-5 flex flex-wrap gap-y-5">
    <!-- Quantity for non-variant-based products -->
    @if (!isVariantBased) {
      <div class="w-full px-2.5">
        <app-text-input [control]="form.controls.quantity" [name]="'qty'" [placeholder]="'Qty'" type="number" label="Quantity"></app-text-input>
      </div>
    }

    <!-- Manual amount for products without price -->
    @if (selectedProduct && !selectedProduct.price) {
      <div class="w-full px-2.5">
        <app-text-input [mask]="'separator.2'" [thousandSeparator]="','" [allowNegativeNumbers]="true" [control]="form.controls.amount" [name]="'amount'" [placeholder]="'0.00'" label="Amount" prefix="£"></app-text-input>
      </div>
    }

    <!-- VG-396: Multi-variant selections grouped by variant -->
    @if (hasVariants) {
      <div class="w-full px-2.5">
        <!-- Spacing to replace removed heading -->
        <div class="mb-3"></div>
        @if (selectedProduct?.is_variant_based && totalVariantQuantity > 0) {
          <div class="flex justify-end text-sm text-gray-600 dark:text-gray-300 mb-2">
            Total quantity: {{ totalVariantQuantity }}
          </div>
        }
        @for (entry of getVariantSelectionsByVariant() | keyvalue; track entry.key; let isLast = $last) {
          @let variantData = entry.value;
          @let variant = variantData.variant;
          <div class="mb-8">
            <!-- Variant header -->
            <div class="flex items-center justify-between mb-3">
              <label class="block text-xl font-semibold text-gray-900 dark:text-gray-100">
                <app-variant-pricing-label
                  [variantId]="variant.id!"
                  [variantName]="variant.name"
                  [product]="selectedProduct!"
                  [variantSelectionsFormArray]="form.controls.variant_selections">
                </app-variant-pricing-label>
              </label>
            </div>

            <!-- Check if variant has no options (accommodation case) -->
            @if (hasNoOptions(variant)) {
              <!-- Simplified UI for 0-option variants: only show notes -->
              @for (selectionIndex of variantData.indices; track selectionIndex) {
                @let variantControl = form.controls.variant_selections.at(selectionIndex);
                <div class="mb-3">
                  <app-text-input
                    [control]="getNotesControl(variantControl)"
                    label="Notes (optional)"
                    [name]="'notes_' + selectionIndex">
                  </app-text-input>
                </div>
              }
            } @else {
              <!-- Full UI for variants with options -->
              <!-- Existing selections for this variant -->
              @for (selectionIndex of variantData.indices; track selectionIndex) {
                @let variantControl = form.controls.variant_selections.at(selectionIndex);
                <div class="mb-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div class="space-y-4">
                    <!-- First row: Option select, Quantity, and Remove button -->
                    <div class="grid grid-cols-1 md:grid-cols-12 gap-4">
                      <!-- Option select -->
                      <div class="md:col-span-7">
                        <app-select
                          [height]="'11'"
                          [control]="getVariantOptionControl(variantControl)"
                          [choices]="getVariantOptionsChoices(variant.id!)"
                          placeholder="Select option..."
                          label="Option"
                          [allowWrap]="true">
                        </app-select>
                        @let info = getOptionInfo(variantControl);
                        @if (info) {
                          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ info }}</p>
                        }
                      </div>

                      <!-- Quantity -->
                      <div class="md:col-span-3">
                      <app-text-input
                        [control]="getQuantityControl(variantControl)"
                        label="Quantity"
                        type="number"
                        placeholder="Qty"
                        [max]="getSelectionMax(variantControl)"
                        [helpText]="getSelectionCapHelp(variantControl)"
                        [name]="'quantity_' + selectionIndex">
                      </app-text-input>
                      @if (getQuantityControl(variantControl).errors?.['server']) {
                        <p class="text-xs text-red-500 mt-1">
                          {{ getQuantityControl(variantControl).errors?.['server'] }}
                        </p>
                      }
                      </div>

                      <!-- Remove button -->
                      <div class="md:col-span-2 flex items-end">
                        <button
                          type="button"
                          (click)="removeVariantOption(selectionIndex)"
                          class="w-full h-11 flex items-center justify-center px-3 py-2 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                          <lucide-icon [size]="16" [name]="Trash2"></lucide-icon>
                        </button>
                      </div>
                    </div>

                    <!-- Second row: Notes -->
                    <div>
                      <app-text-input
                        [control]="getNotesControl(variantControl)"
                        label="Notes (optional)"
                        [name]="'notes_' + selectionIndex">
                      </app-text-input>
                    </div>
                  </div>
                </div>
              }

              <!-- Add button for this variant (hide only if variant has exactly 1 option) -->
              @if (variant.options && variant.options.length !== 0) {
                <button
                  type="button"
                  (click)="addVariantOption(variant)"
                  class="w-full px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors flex items-center justify-center gap-2">
                  <lucide-icon [size]="16" [name]="Plus"></lucide-icon>
                  <span>Add {{ variant.name }}</span>
                </button>
              }
            }

            <!-- Elegant separator (except for last variant) -->
            @if (!isLast) {
              <div class="mt-8 flex items-center justify-center">
                <div class="w-32 h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
              </div>
            }
          </div>
        }
      </div>
    }
  </div>
}

<!-- Pricing Panel (sticky at bottom) -->
@if (shouldShowPricingSection) {
  <app-product-item-pricing
    [product]="selectedProduct!"
    [eventId]="event.id"
    [variantSelectionsFormArray]="form.controls.variant_selections"
    [quantityControl]="form.controls.quantity"
    [amountControl]="form.controls.amount">
  </app-product-item-pricing>
}

<!-- Action Button -->
<div class="-mx-2.5 mt-10">
  <div class="flex flex-row w-full justify-between align-bottom">
    <div class="flex justify-end px-2.5">
      <app-button [label]="submitLabel" [disabled]="!form.valid || form.pristine" (click)="save()"></app-button>
    </div>
  </div>
</div>
